import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const PRODUCT_TYPES = ['PLAN', 'RESOURCE_PACK', 'SERVICE'] as const;
const TARGET_SCOPES = ['PERSONAL', 'TEAM', 'ENTERPRISE', 'ALL'] as const;
const PRODUCT_STATUSES = ['DRAFT', 'ACTIVE', 'INACTIVE'] as const;

const PRODUCT_TYPE_LABELS: Record<(typeof PRODUCT_TYPES)[number], string> = {
  PLAN: '套餐',
  RESOURCE_PACK: '资源包',
  SERVICE: '服务',
};

const productSchema = z.object({
  code: z
    .string()
    .min(2, '产品编码不能为空')
    .max(50, '产品编码长度不能超过50字符')
    .regex(/^[A-Z_]+$/, '产品编码只能使用大写字母与下划线'),
  name: z.string().min(1, '产品名称不能为空').max(100, '产品名称长度不能超过100字符'),
  type: z.enum(PRODUCT_TYPES),
  targetScope: z.enum(TARGET_SCOPES),
  status: z.enum(PRODUCT_STATUSES),
});

type ProductFormValues = z.infer<typeof productSchema>;

export type ProductFormItem = {
  id?: string;
  code?: string | null;
  name?: string | null;
  type?: string | null;
  targetScope?: string | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
};

interface ProductEditorModalProps {
  open: boolean;
  product?: ProductFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function buildDefaultValues(product?: ProductFormItem | null): ProductFormValues {
  if (product) {
    const type = (PRODUCT_TYPES as readonly string[]).includes(product.type ?? '')
      ? (product.type as (typeof PRODUCT_TYPES)[number])
      : 'PLAN';
    return {
      code: product.code || '',
      name: product.name || '',
      type,
      targetScope: (TARGET_SCOPES as readonly string[]).includes(product.targetScope ?? '')
        ? (product.targetScope as (typeof TARGET_SCOPES)[number])
        : 'ALL',
      status: (PRODUCT_STATUSES as readonly string[]).includes(product.status ?? '')
        ? (product.status as (typeof PRODUCT_STATUSES)[number])
        : 'DRAFT',
    };
  }

  return {
    code: '',
    name: '',
    type: 'PLAN',
    targetScope: 'ALL',
    status: 'DRAFT',
  };
}

export function ProductEditorModal({ open, product, onCancel, onSuccess }: ProductEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!product;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema) as any,
    defaultValues: buildDefaultValues(null),
    mode: 'onChange',
  });
  const { reset } = form;

  const createProductMutation = trpc.bizProduct.create.useMutation({
    onSuccess: () => {
      toast.success('产品创建成功');
      form.reset();
      onSuccess();
      void utils.bizProduct.list.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateProductMutation = trpc.bizProduct.update.useMutation({
    onSuccess: () => {
      toast.success('产品更新成功');
      form.reset();
      onSuccess();
      void utils.bizProduct.list.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  useEffect(() => {
    if (!open) {
      reset(buildDefaultValues(null));
      return;
    }
    reset(buildDefaultValues(isEdit ? product : null));
  }, [open, isEdit, product, reset]);

  const onSubmit = async (values: ProductFormValues) => {
    const payload = {
      code: values.code,
      name: values.name,
      type: values.type,
      targetScope: values.targetScope,
      status: values.status,
    };

    if (isEdit && product?.id) {
      await updateProductMutation.mutateAsync({
        id: product.id,
        data: payload,
      });
    } else {
      await createProductMutation.mutateAsync(payload);
    }
  };

  const isLoading = createProductMutation.isPending || updateProductMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg" className="max-w-2xl">
          <Modal.Dialog
            key={
              open ? (isEdit && product ? `edit-${String(product.id ?? '')}` : 'create') : 'closed'
            }
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑产品' : '创建产品'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改产品信息' : '填写产品信息以创建新产品'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-product-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.code}>
                      <Label className="text-sm font-medium">
                        产品编码 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={50}
                        placeholder="请输入产品编码（如：SAAS_FREE、SAAS_BASIC）"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        只能使用大写字母与下划线，如：PLAN_FREE
                      </Description>
                      {errors.code ? <FieldError>{errors.code.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.name}>
                      <Label className="text-sm font-medium">
                        产品名称 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="请输入产品名称"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.type}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        产品类型 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {PRODUCT_TYPES.map((t) => (
                            <ListBox.Item key={t} id={t} textValue={PRODUCT_TYPE_LABELS[t]}>
                              {PRODUCT_TYPE_LABELS[t]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.type ? <FieldError>{errors.type.message}</FieldError> : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="targetScope"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.targetScope}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        适用空间 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {TARGET_SCOPES.map((scope) => (
                            <ListBox.Item key={scope} id={scope} textValue={scope}>
                              {scope}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.targetScope ? (
                        <FieldError>{errors.targetScope.message}</FieldError>
                      ) : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.status}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        状态 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {PRODUCT_STATUSES.map((status) => (
                            <ListBox.Item key={status} id={status} textValue={status}>
                              {status}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.status ? <FieldError>{errors.status.message}</FieldError> : null}
                    </Select>
                  )}
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
